import { Alert, Snackbar } from '@mui/material';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type ToastSeverity = 'success' | 'info' | 'warning' | 'error';

interface ToastOptions {
  title: string;
  message?: string;
  severity?: ToastSeverity;
}

type ShowToast = (options: ToastOptions) => void;

const ToastContext = createContext<ShowToast | null>(null);

/**
 * App-wide toast/snackbar. Mounted once in AppLayout so any page can call
 * `useToast()` instead of holding its own local Snackbar state (which
 * ProjectDetail.tsx and DocumentEditorPage.tsx previously did independently).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<ToastOptions>({ title: '' });

  const show = useCallback<ShowToast>((options) => {
    setToast(options);
    setOpen(true);
  }, []);

  const handleClose = useCallback((_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  }, []);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleClose}
          severity={toast.severity ?? 'success'}
          variant="filled"
          sx={{ minWidth: 280 }}
        >
          <strong>{toast.title}</strong>
          {toast.message && <div>{toast.message}</div>}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ShowToast {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
